/* eslint-disable */
import { Amplify } from 'aws-amplify';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from './environments/environment';
import 'aws-amplify/auth/enable-oauth-listener';
import { Injector } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { first } from 'rxjs/operators';

import { GtmService } from 'app/shared/services/gtm.service';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { ProfileStateService } from 'app/shared/services/profile-state.service';
import { MediaService } from 'app/shared/services/media.service';
import { TokensService } from 'app/shared/services/tokens.service';
import { AuthService } from 'app/auth/auth.service';

import { ELocalStorageKey } from 'app/shared/enums/shared.enums';
import { EUserAuthType } from 'app/auth/auth.enums';
import { EProfileState } from 'app/enums/profile.enums';

GtmService.init();

const analyticsService = new AnalyticsService();

let appInjector: Injector;

bootstrapApplication(AppComponent, appConfig)
  .then(appRef => {
    appInjector = appRef.injector;
  })
  .catch(err => console.error(err));

Amplify.configure({
  Auth: {
    Cognito: {
      loginWith: {
        oauth: {
          responseType: 'code',
          redirectSignIn: JSON.parse(environment.redirectSignIn),
          redirectSignOut: JSON.parse(environment.redirectSignOut),
          domain: environment.domain,
          scopes: ['email', 'openid']
        }
      },
      userPoolClientId: environment.userPoolClientId,
      userPoolId: environment.userPoolId
    }
  }
});

const updateUserAuthState = (isAuthenticated: boolean): void => {
  if (appInjector) {
    const authService = appInjector.get(AuthService);
    authService.$isUser.next(isAuthenticated);
  }
};

const handleTokenRefresh = async (): Promise<void> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    const accessToken = session.tokens?.accessToken?.toString();

    if (idToken) {
      localStorage.setItem('accessToken', idToken);
    }
    if (accessToken) {
      localStorage.setItem('cognitoAccessToken', accessToken);
    }

    updateUserAuthState(true);
  } catch (error) {
    updateUserAuthState(false);
  }
};

Hub.listen('auth', async ({ payload }) => {
  switch (payload.event) {
    case 'signInWithRedirect':
      fetchAuthSession().then(async res => {
        const userId = res.tokens.idToken.payload['stxt_sub'] as string;
        const email = res.tokens.idToken.payload['email'] as string;

        localStorage.setItem(
          ELocalStorageKey.User,
          JSON.stringify({
            username: email,
            type: EUserAuthType.Google_Sign_In
          })
        );

        // Store user ID for later use
        localStorage.setItem(ELocalStorageKey.UserId, userId);

        analyticsService.onboardingProfile(EProfileState.PROFILE_EMAIL_VERIFICATION, userId);

        const stxtCreatedAt = res.tokens.idToken.payload['stxt_created_at'] as string;
        const createdAt =
          stxtCreatedAt === 'undefined' || stxtCreatedAt === undefined ? 0 : Number(stxtCreatedAt);

        const currentTime = new Date().getTime() / 1000;
        const differenceInSeconds = currentTime - createdAt;

        differenceInSeconds < 30
          ? analyticsService.signUp(userId, email)
          : analyticsService.login(email, email, userId);

        // Store tokens in localStorage
        const idToken = res.tokens?.idToken?.toString();
        const accessToken = res.tokens?.accessToken?.toString();

        if (idToken) {
          localStorage.setItem('accessToken', idToken);
        }
        if (accessToken) {
          localStorage.setItem('cognitoAccessToken', accessToken);
        }

        if (appInjector) {
          // Get services from the application injector
          const profileStateService = appInjector.get(ProfileStateService);
          const mediaService = appInjector.get(MediaService);
          const tokensService = appInjector.get(TokensService);

          // Make the necessary API calls
          await Promise.all([
            profileStateService.prefetchProfileData().pipe(first()).toPromise(),
            mediaService.initialLoad().pipe(first()).toPromise(),
            tokensService.fetchTokensBalance().pipe(first()).toPromise()
          ]);
        }
      });
      break;
    case 'signInWithRedirect_failure':
      // handle sign in failure
      localStorage.removeItem(ELocalStorageKey.RedirectUrl);
      console.log('failed google auth');
      break;
    case 'tokenRefresh':
      await handleTokenRefresh();
      break;
    case 'customOAuthState':
      const customState = payload.data;
      console.log(customState);
      break;
  }
});
Sentry.init({
  dsn: environment.sentryDsn,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 0.8,
  environment: environment.serviceEnvironment,
  tracePropagationTargets: [
    'localhost',
    'https://staging.stxt.ai',
    'https://develop.stxt.ai',
    'https://app.stxt.ai'
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
});
